import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Input, Table, message, Space, Tag, Tabs, Spin } from 'antd';
import { PlayCircleOutlined, DatabaseOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import api from '../../lib/api';
import { useAppSelector } from '../../store/hooks';
import SqlEditor from '../../components/SqlEditor';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

interface DataSource {
  id: string;
  name: string;
  type: string;
  status: string;
  description?: string;
}

interface TableSchema {
  tableName: string;
  columns: Array<{ name: string; type: string }>;
}

const McpServerPage: React.FC = () => {
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [selectedDataSource, setSelectedDataSource] = useState<string>('');
  const [sqlQuery, setSqlQuery] = useState<string>('');
  const [queryResult, setQueryResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  
  // Natural language question state
  const [naturalQuestion, setNaturalQuestion] = useState<string>('');
  const [llmAnswer, setLlmAnswer] = useState<any>(null);
  const [llmLoading, setLlmLoading] = useState(false);
  
  // Schema autocomplete state
  const [schemaData, setSchemaData] = useState<TableSchema[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [schemaCached, setSchemaCached] = useState(false);
  
  // Get current project from Redux
  const { currentProject } = useAppSelector((state) => state.project);

  useEffect(() => {
    if (currentProject) {
      loadDataSources();
    }
  }, [currentProject]);

  useEffect(() => {
    if (selectedDataSource) {
      fetchSchemaForAutocomplete();
    }
  }, [selectedDataSource]);

  const loadDataSources = async () => {
    if (!currentProject) {
      message.warning('Please select a project first');
      return;
    }

    try {
      // Use project-specific API like Data Sources page does
      const response = await api.get(`/projects/${currentProject.id}/data-sources`);
      const dbSources = response.data
        .filter((ds: any) =>
          ['mssql', 'mysql', 'postgres', 'mariadb', 'oracle'].includes(ds.connectorType)
        )
        .map((ds: any) => ({
          id: ds.id,
          name: ds.name,
          type: ds.connectorType, // Map connectorType to type
          status: 'active', // Default to active
          description: ds.description,
        }));
      setDataSources(dbSources);
    } catch (error) {
      message.error('Failed to load data sources');
      console.error(error);
    }
  };

  const fetchSchemaForAutocomplete = async () => {
    setSchemaLoading(true);
    try {
      // Try to get schema from cache first
      const statusResponse = await api.post('/mcp/schema/status', {
        dataSourceId: selectedDataSource,
      });

      if (statusResponse.data.cached && !statusResponse.data.isStale) {
        // Use cached schema - just get table names, NO describe-table calls
        setSchemaCached(true);
        const tablesResponse = await api.post('/mcp/tools/list-tables', {
          dataSourceId: selectedDataSource,
        });

        // Only store table names - no need to describe each table
        // SQL autocomplete will work with just table names
        const tables = tablesResponse.data.tables.map((table: any) => ({
          tableName: table.name,
          columns: [], // Empty columns - will be populated on-demand if needed
        }));

        setSchemaData(tables);
      } else {
        // Sync schema first
        setSchemaCached(false);
        await syncSchema(false);
      }
    } catch (error) {
      console.error('Failed to fetch schema for autocomplete:', error);
      setSchemaCached(false);
    } finally {
      setSchemaLoading(false);
    }
  };

  const syncSchema = async (showMessage = true) => {
    setSchemaLoading(true);
    try {
      await api.post('/mcp/schema/sync', {
        dataSourceId: selectedDataSource,
      });

      if (showMessage) {
        message.success('Schema synced successfully!');
      }
      
      setSchemaCached(true);
      
      // Fetch schema after sync
      await fetchSchemaForAutocomplete();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to sync schema');
    } finally {
      setSchemaLoading(false);
    }
  };

  const askQuestion = async () => {
    if (!selectedDataSource) {
      message.warning('Please select a data source');
      return;
    }
    if (!naturalQuestion.trim()) {
      message.warning('Please enter a question');
      return;
    }

    setLlmLoading(true);
    setLlmAnswer(null);
    try {
      const response = await api.post('/mcp/ask', {
        question: naturalQuestion,
        dataSourceId: selectedDataSource,
      });

      setLlmAnswer(response.data);
      message.success('Question answered by LLM');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to get answer');
      setLlmAnswer({
        error: error.response?.data?.message || error.message,
      });
    } finally {
      setLlmLoading(false);
    }
  };

  const executeQuery = async () => {
    if (!selectedDataSource) {
      message.warning('Please select a data source');
      return;
    }
    if (!sqlQuery.trim()) {
      message.warning('Please enter a SQL query');
      return;
    }

    setLoading(true);
    try {
      // Internal UI uses session auth (Bearer token) automatically via api instance
      const response = await api.post('/mcp/query', {
        dataSourceId: selectedDataSource,
        query: sqlQuery,
        outputFormat: 'json',
        resultLimit: 1000,
      });

      setQueryResult(response.data);
      message.success('Query executed successfully');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Query execution failed');
      setQueryResult({
        success: false,
        error: error.response?.data?.message || error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderResultTable = () => {
    if (!queryResult || !queryResult.success) return null;

    const data = Array.isArray(queryResult.data) ? queryResult.data : [];
    if (data.length === 0) return <p>No results found</p>;

    const columns = Object.keys(data[0]).map((key) => ({
      title: key,
      dataIndex: key,
      key: key,
      ellipsis: true,
    }));

    return (
      <Table
        columns={columns}
        dataSource={data.map((row: any, idx: number) => ({ ...row, key: idx }))}
        scroll={{ x: 'max-content', y: 400 }}
        pagination={{ pageSize: 50 }}
        size="small"
      />
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <DatabaseOutlined />
            MCP Server - SQL Query Interface
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={loadDataSources}>
            Refresh
          </Button>
        }
      >
        <Tabs defaultActiveKey="ask">
          <TabPane
            tab={
              <span>
                🤖 Ask Question
              </span>
            }
            key="ask"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Data Source Selection */}
              <Card size="small" title="Select Data Source">
                {!currentProject ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                    Please select a project from the top bar to view data sources
                  </div>
                ) : dataSources.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                    No database data sources found in project: {currentProject.name}
                  </div>
                ) : (
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Choose a database connection"
                    value={selectedDataSource}
                    onChange={setSelectedDataSource}
                  >
                    {dataSources.map((ds) => (
                      <Option key={ds.id}value={ds.id}>
                        <Space>
                          <Tag color={ds.status === 'active' ? 'green' : 'red'}>
                            {ds.type.toUpperCase()}
                          </Tag>
                          {ds.name}
                        </Space>
                      </Option>
                    ))}
                  </Select>
                )}
              </Card>

              {/* Natural Language Question */}
              <Card size="small" title="Ask Your Question (in Natural Language)">
                <TextArea
                  rows={4}
                  placeholder="e.g., Show me the top 10 customers by revenue&#x0a;e.g., How many orders were placed last month?&#x0a;e.g., What products are selling the most?"
                  value={naturalQuestion}
                  onChange={(e) => setNaturalQuestion(e.target.value)}
                />
                <div style={{ marginTop: '12px' }}>
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={askQuestion}
                    loading={llmLoading}
                    disabled={!selectedDataSource || !naturalQuestion.trim()}
                  >
                    Ask LLM
                  </Button>
                </div>
              </Card>

              {/* LLM Answer */}
              {llmAnswer && (
                <Card
                  size="small"
                  title={
                    <Space>
                      Answer
                      {llmAnswer.error ? (
                        <Tag color="error">Error</Tag>
                      ) : (
                        <Tag color="success">Success</Tag>
                      )}
                    </Space>
                  }
                >
                  {llmAnswer.error ? (
                    <div style={{ color: 'red' }}>
                      <p>Error: {llmAnswer.error}</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <strong>Answer:</strong>
                        <p style={{ marginTop: '8px', whiteSpace: 'pre-wrap' }}>{llmAnswer.answer}</p>
                      </div>

                      {llmAnswer.executedQuery && (
                        <div style={{ marginBottom: '16px' }}>
                          <strong>SQL Query Used:</strong>
                          <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px', overflow: 'auto' }}>
                            {llmAnswer.executedQuery}
                          </pre>
                        </div>
                      )}

                      {llmAnswer.toolCalls && llmAnswer.toolCalls.length > 0 && (
                        <div>
                          <strong>LLM Tool Calls ({llmAnswer.toolCalls.length}):</strong>
                          <div style={{ marginTop: '8px' }}>
                            {llmAnswer.toolCalls.map((call: any, idx: number) => (
                              <Tag key={idx} style={{ marginBottom: '4px' }}>
                                {idx + 1}. {call.tool}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card>
              )}
            </Space>
          </TabPane>

          <TabPane
            tab={
              <span>
                <PlayCircleOutlined />
                Direct SQL
              </span>
            }
            key="query"
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Data Source Selection */}
              <Card size="small" title="Select Data Source">
                {!currentProject ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                    Please select a project from the top bar to view data sources
                  </div>
                ) : dataSources.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#999' }}>
                    No database data sources found in project: {currentProject.name}
                  </div>
                ) : (
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Choose a database connection"
                    value={selectedDataSource}
                    onChange={setSelectedDataSource}
                  >
                    {dataSources.map((ds) => (
                      <Option key={ds.id} value={ds.id}>
                        <Space>
                          <Tag color={ds.status === 'active' ? 'green' : 'red'}>
                            {ds.type.toUpperCase()}
                          </Tag>
                          {ds.name}
                        </Space>
                      </Option>
                    ))}
                  </Select>
                )}
              </Card>

              {/* SQL Query Input */}
              <Card 
                size="small" 
                title="SQL Query"
                extra={
                  selectedDataSource && (
                    <Button
                      size="small"
                      icon={<SyncOutlined spin={schemaLoading} />}
                      onClick={() => syncSchema(true)}
                      loading={schemaLoading}
                    >
                      {schemaCached ? 'Refresh Schema' : 'Sync Schema'}
                    </Button>
                  )
                }
              >
                {schemaLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin /> Loading schema for autocomplete...
                  </div>
                ) : (
                  <SqlEditor
                    value={sqlQuery}
                    onChange={setSqlQuery}
                    height="300px"
                    tableSuggestions={schemaData}
                  />
                )}
                <div style={{ marginTop: '12px', fontSize: 12, color: '#8c8c8c' }}>
                  💡 Press Ctrl+Space for autocomplete • {schemaData.length} tables loaded
                  {schemaCached && <Tag color="green" style={{ marginLeft: 8 }}>Cached</Tag>}
                </div>
                <div style={{ marginTop: '12px' }}>
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={executeQuery}
                    loading={loading}
                    disabled={!selectedDataSource || !sqlQuery.trim()}
                  >
                    Execute Query
                  </Button>
                </div>
              </Card>

              {/* Query Results */}
              {queryResult && (
                <Card
                  size="small"
                  title={
                    <Space>
                      Results
                      {queryResult.success ? (
                        <Tag color="success">Success</Tag>
                      ) : (
                        <Tag color="error">Error</Tag>
                      )}
                    </Space>
                  }
                >
                  {queryResult.success ? (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <Space>
                          <span>
                            Execution Time: {queryResult.executionTime}ms
                          </span>
                          {queryResult.rowsAffected && (
                            <span>
                              Rows Affected: {queryResult.rowsAffected.join(', ')}
                            </span>
                          )}
                        </Space>
                      </div>
                      {renderResultTable()}
                    </>
                  ) : (
                    <div style={{ color: 'red' }}>
                      <p>Error: {queryResult.error || queryResult.message}</p>
                    </div>
                  )}
                </Card>
              )}
            </Space>
          </TabPane>

          <TabPane
            tab={
              <span>
                <DatabaseOutlined />
                API Documentation
              </span>
            }
            key="docs"
          >
            <Card>
              <h3>MCP Server API Endpoints</h3>

              <h4>1. Execute Query</h4>
              <pre style={{ background: '#f5f5f5', padding: '12px' }}>
                {`POST /mcp/query
Headers: x-api-key: your_mcp_api_key

Body:
{
  "dataSourceId": "ds_123456",
  "query": "SELECT * FROM users LIMIT 10",
  "outputFormat": "json",
  "resultLimit": 1000
}`}
              </pre>

              <h4>2. List Data Sources</h4>
              <pre style={{ background: '#f5f5f5', padding: '12px' }}>
                {`POST /mcp/datasources
Headers: x-api-key: your_mcp_api_key

Body:
{
  "projectId": "proj_123456",
  "filterType": ["mssql", "mysql"]
}`}
              </pre>

              <h4>3. Test Connection</h4>
              <pre style={{ background: '#f5f5f5', padding: '12px' }}>
                {`POST /mcp/test-connection
Headers: x-api-key: your_mcp_api_key

Body:
{
  "connectorType": "mssql",
  "config": {
    "server": "localhost",
    "database": "mydb",
    "user": "admin",
    "password": "password"
  }
}`}
              </pre>

              <h4>4. Get Schema</h4>
              <pre style={{ background: '#f5f5f5', padding: '12px' }}>
                {`POST /mcp/schema
Headers: x-api-key: your_mcp_api_key

Body:
{
  "dataSourceId": "ds_123456"
}`}
              </pre>

              <h4>API Key Scopes Required</h4>
              <ul>
                <li>
                  <code>mcp:query</code> - Execute SQL queries
                </li>
                <li>
                  <code>mcp:datasource</code> - List and manage data sources
                </li>
                <li>
                  <code>mcp:admin</code> - Full MCP server access
                </li>
              </ul>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default McpServerPage;
